import React, { createContext, useContext, useState, useEffect } from 'react';

const UserSettingsContext = createContext();

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};

export const UserSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    autoAddToPantry: false, // Default to false
    pantryDefaultLocation: 'pantry',
    pantryDefaultExpiryDays: 7,
    disableExpiryTracking: false,
    prioritizeRecipes: false
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('userSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        console.log('📥 Loading settings from localStorage:', parsedSettings);
        setSettings(prev => ({ ...prev, ...parsedSettings }));
      }
    } catch (error) {
      console.warn('Failed to load user settings:', error);
    }
    // Mark as initialized after loading
    setIsInitialized(true);
  }, []);

  // Save settings to localStorage whenever they change (but not on initial load)
  useEffect(() => {
    if (isInitialized) {
      try {
        console.log('💾 Saving settings to localStorage:', settings);
        localStorage.setItem('userSettings', JSON.stringify(settings));
      } catch (error) {
        console.warn('Failed to save user settings:', error);
      }
    }
  }, [settings, isInitialized]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  };

  const resetSettings = () => {
    const defaultSettings = {
      autoAddToPantry: false,
      pantryDefaultLocation: 'pantry',
      pantryDefaultExpiryDays: 7,
      disableExpiryTracking: false,
      prioritizeRecipes: false
    };
    setSettings(defaultSettings);
  };

  const value = {
    settings,
    updateSetting,
    updateSettings,
    resetSettings
  };

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export default UserSettingsContext;
